import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import { HOURS, MINUTES, formatTime, parseTime } from "./time-utils";
import TimeColumn from "./TimeColumn";
import styles from "./TimePicker.module.css";

type TimePickerProps = {
  /** Current value in "HH:mm" format */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
};

const timeColumnClassNames = {
  column: styles.column,
  item: styles.item,
  itemSelected: styles.itemSelected,
};

function TimePicker({
  value,
  onChange,
  placeholder = "HH:mm",
  disabled = false,
  error = false,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { hours: initialHours, minutes: initialMinutes } = parseTime(value);
  const [selectedHours, setSelectedHours] = useState(initialHours);
  const [selectedMinutes, setSelectedMinutes] = useState(initialMinutes);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { hours, minutes } = parseTime(value);
    setSelectedHours(hours);
    setSelectedMinutes(minutes);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    if (!disabled) {
      const { hours, minutes } = parseTime(value);
      setSelectedHours(hours);
      setSelectedMinutes(minutes);
      setIsOpen(true);
    }
  }, [disabled, value]);

  const handleCancel = useCallback(() => {
    const { hours, minutes } = parseTime(value);
    setSelectedHours(hours);
    setSelectedMinutes(minutes);
    setIsOpen(false);
  }, [value]);

  const handleSelect = useCallback(() => {
    onChange?.(formatTime(selectedHours, selectedMinutes));
    setIsOpen(false);
  }, [onChange, selectedHours, selectedMinutes]);

  const handleNow = useCallback(() => {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, "0");
    const m = String(now.getMinutes()).padStart(2, "0");
    setSelectedHours(h);
    setSelectedMinutes(m);
  }, []);

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpen();
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [handleOpen]
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger + (error ? ` ${styles.error}` : "")}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Icon name="Time" className={styles.triggerIcon} />
        <span
          className={value ? styles.triggerValue : styles.triggerPlaceholder}
        >
          {value ?? placeholder}
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="dialog" aria-label="Time picker">
          <div className={styles.selectors}>
            <TimeColumn
              items={HOURS}
              selectedValue={selectedHours}
              onSelect={setSelectedHours}
              label="Hours"
              classNames={timeColumnClassNames}
            />
            <span className={styles.separator}>:</span>
            <TimeColumn
              items={MINUTES}
              selectedValue={selectedMinutes}
              onSelect={setSelectedMinutes}
              label="Minutes"
              classNames={timeColumnClassNames}
            />
          </div>

          <div className={styles.footer}>
            <div className={styles.nowAction}>
              <Button
                variant="secondary"
                size="small"
                text="Now"
                icon="Time_and_date"
                onClick={handleNow}
              />
            </div>
            <div className={styles.actions}>
              <Button
                variant="ghost"
                size="small"
                text="Cancel"
                onClick={handleCancel}
              />
              <Button
                variant="primary"
                size="small"
                text="Select"
                onClick={handleSelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimePicker;
